<template>
    <v-card class="mb-2" elevation="0" v-if="bankFlows.datasets">
        <v-row class="d-flex">
            <v-col cols="11">
                <div>
                    <LineChart
                        v-if="displayData"
                        :data="bankFlows"
                        :options="options"
                        :height="bankFlows.datasets.length * 30"
                    />
                    <template v-else>
                        <v-row no-gutters>
                            <v-col class="d-flex justify-center"> Aucune donnée disponible </v-col>
                        </v-row>
                    </template>
                </div>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col
                md="11"
                cols="12"
                :class="{ 'pl-13': !$vuetify.display.mobile, 'px-2': $vuetify.display.mobile }"
            >
                <v-range-slider
                    v-model="dateRange"
                    min="0"
                    :max="dates.length - 1"
                    track-color="#CCC"
                    :ticks="dateTicksLabels"
                >
                    <template #tick-label="{ tick: { value, position, label }, index: number }">
                        <div
                            v-if="$vuetify.display.mobile"
                            class="mt-3"
                            style="transform: rotate(60deg); font-size: 0.7rem"
                        >
                            {{ label }}
                        </div>
                        <div v-else>
                            {{ label }}
                        </div>
                    </template>
                </v-range-slider>
            </v-col>
            <v-col md="1" cols="12" class="d-flex justify-end flex-row">
                <v-btn
                    :class="{
                        'ml-4 mr-2 mt-2': !$vuetify.display.mobile,
                        'mx-2 my-2': $vuetify.display.mobile,
                    }"
                    @click="setSlider()"
                    size="x-small"
                    >rafraichir</v-btn
                >
            </v-col>
        </v-row>
    </v-card>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";

import { Line as LineChart } from "vue-chartjs";
import moment, { unitOfTime } from "moment-timezone";
import BANK_FLOW from "../graphql/BankFlow.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    components: {
        LineChart,
    },
    emits: ["graphDate"],
})
export class BankFlow extends Vue {
    @Prop() companyId;
    @Prop() selectedAccount;
    @Prop() startDates;

    dateRange: any = [];
    unitOfTime: unitOfTime.StartOf = "day";

    userStart: any = moment().startOf("year").startOf(this.unitOfTime);
    userEnd: any = moment().endOf("year").endOf(this.unitOfTime);

    dates = [];
    dateTicksLabels = {};
    startDatesBool: boolean = false;

    @Apollo({
        query: BANK_FLOW,
        variables() {
            return {
                companyId: this.companyId,
                startDate: this.userStart, // this.startDates.startDate,
                endDate: this.userEnd, // this.startDates.endDate,
                unitOfTime: this.unitOfTime,
                selectedAccount: this.selectedAccount,
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.bankFlow?.result) {
                this.bankFlows = data.bankFlow.result; // JSON.parse(JSON.stringify(data.bankFLow.result))
                this.optionData(this.bankFlows?.datasets);
            } else {
                this.bankFlows = [];
            }
        },
    })
    bankFlows: any = [];

    beforeMount() {
        this.resetSlider();
    }

    @Watch("startDates")
    startDatesChanged() {
        if (
            !this.dates.includes(moment(this.startDates.startDate).format("MM-YY")) &&
            !this.dates.includes(moment(this.startDates.endDate).format("MM-YY"))
        ) {
            this.startDatesBool = false;
            this.resetSlider();
            this.setSlider();
        } else if (
            !this.dates.includes(moment(this.startDates.startDate).format("MM-YY")) ||
            !this.dates.includes(moment(this.startDates.endDate).format("MM-YY"))
        ) {
            this.startDatesBool = true;
            this.resetSlider();
            this.setSlider();
        } else {
            this.resetSlider();
            this.startDatesBool = false;
        }
    }

    resetSlider() {
        const start = moment(this.startDates.startDate).startOf("month");
        const end = moment(this.startDates.endDate).endOf("month");
        const count = end.diff(start, "months") + 1;
        const current = start.clone();
        this.dates = new Array(count).fill("");
        for (let i = 0; i < count; i++) {
            this.dates[i] = current.format("MM-YY");
            current.add(1, "month");
            if (i === count - 1) {
                this.dates.push("");
            }
        }
        if (!this.dateRange.length) {
            this.dateRange.push(0);
            this.dateRange.push(this.dates.length - 1);
        } else if (this.startDatesBool) {
            this.dateRange = [0, this.dates.length - 1];
        }
        this.dateTicksLabels = Object.assign({}, this.dates);
    }

    @Watch("dateRange", { deep: true })
    slideDateChanged() {
        this.userStart = moment(this.dates[this.dateRange[0]], "MM-YY").format("YYYY-MM-DD");
        if (this.dates[this.dateRange[1]]) {
            this.userEnd = moment(this.dates[this.dateRange[1]], "MM-YY")
                .subtract(1, "months")
                .endOf("month")
                .format("YYYY-MM-DD");
        } else {
            this.userEnd = moment(this.dates[this.dateRange[1] - 1], "MM-YY")
                .endOf("month")
                .format("YYYY-MM-DD");
        }
        this.$emit("graphDate", { startDate: this.userStart, endDate: this.userEnd });
    }

    setSlider() {
        this.dateRange = [0, this.dates.length - 1];
        // this.slideDateChanged()
    }

    get displayData() {
        if (!this.bankFlows?.datasets?.length) {
            return false;
        }
        return true;
    }

    optionData(datasets) {
        if (datasets) {
            for (const data of datasets) {
                data.borderWidth = 1;
                data.fill = false;
                data.showLine = false;
                data.order = 0;
                if (data.label === "COLLISION") {
                    data.borderColor = "rgba(255, 40, 40, 1)";
                    data.backgroundColor = "rgba(255, 40, 40, 0.2)";
                } else {
                    data.borderColor = "rgba(249, 177, 42, 1)"; // (38, 38, 38, 1) // (95, 201, 184, 1)
                    data.backgroundColor = "rgba(249, 177, 42, 0.2)"; // (38, 38, 38, 0.2) // (95, 201, 184, 0.2)
                }
            }
        }
    }

    get options() {
        const self = this;
        return {
            responsive: true,
            onClick: function (e, array) {
                if (array.length != 0) {
                    const range = self.dates.indexOf(
                        moment(self.bankFlows.labels[array[0].index])
                            .startOf("month")
                            .format("MM-YY"),
                    );
                    self.dateRange = [range, range + 1];
                    self.$emit("graphDate", {
                        startDate: moment(self.bankFlows.labels[array[0].index])
                            .startOf("month")
                            .format("YYYY-MM-DD"),
                        endDate: moment(self.bankFlows.labels[array[0].index])
                            .endOf("month")
                            .format("YYYY-MM-DD"),
                    });
                }
            },
            plugins: {
                legend: {
                    display: false,
                    position: "top",
                },
                title: {
                    display: false,
                    text: "Flux Bancaire",
                },
                tooltip: {
                    mode: "index",
                    position: "average",
                    boxHeight: 4,
                    boxWidth: 4,
                    bodyFont: {
                        size: 9,
                    },
                    titleFont: {
                        size: 8,
                    },
                    callbacks: {
                        label: function (tooltipItem: any): any {
                            let label = self.$t(`bankFlow.flow.${tooltipItem.dataset.label}`);
                            return label;
                        },
                    },
                },
            },
            scales: {
                x: {
                    display: false,
                    grid: {
                        display: false,
                    },
                },
                y: {
                    // display: false,
                    beginAtZero: true,
                    ticks: {
                        callback: (value, index, values) => {
                            const yLabels = [];
                            if (this.bankFlows?.datasets?.length) {
                                for (const data of this.bankFlows.datasets) {
                                    yLabels.push(self.$t(`bankFlow.flow.${data.label}`));
                                }
                                if (value >= 0 && value < yLabels.length) {
                                    return yLabels[value];
                                }
                            }
                        },
                    },
                },
            },
        };
    }
}

export default toNative(BankFlow);
</script>
<style lang="css">
/** Removes first tick margin. Useful on mobile to avoid first and second tick overlapping */
.v-slider-track__tick--first {
    margin-inline-start: 0px !important;
    -webkit-margin-inline-start: 0px !important;
}
</style>
